import { apiService } from "./api.service";
import api_no_token_Service from "./api_no_token.service";
import { PaginatedResponse } from "types/index";
import { Company } from "types/company";
import { readCompanyIndustryId } from "utils/companyIndustry";
import { normalizePaginatedResponse } from "utils/pagination";
import { unwrapEntity, unwrapOrThrow } from "utils/apiResponse";

function normalizeCompanyPayload(raw: Record<string, unknown>): Company {
  const industryRaw = readCompanyIndustryId(raw);
  const industryId = industryRaw ? Number(industryRaw) : undefined;
  return {
    ...(raw as Company),
    ...(industryId != null && !Number.isNaN(industryId) ? { industryId } : {}),
  };
}

class CompaniesService {
  async getAll(): Promise<Company[]> {
    const response = await apiService.get<any>(`/company/GetAll`, {
      // Do not trigger global loader when fetching companies for license modal
      // to avoid blocking UI with a page-level spinner.
      metadata: { skipGlobalLoading: true },
    } as any);

    const payload: any = (response as any)?.data ?? response;
    if (Array.isArray(payload)) {
      return payload as Company[];
    }
    if (Array.isArray((payload as any)?.data)) {
      return (payload as any).data as Company[];
    }
    if (Array.isArray((response as any)?.items)) {
      return (response as any).items as Company[];
    }
    throw new Error((response as any)?.message || "Failed to load companies");
  }

  async list(
    page: number = 1,
    limit: number = 100,
    options?: {
      search?: string;
      packageId?: string | number;
      fromDate?: string | null;
      toDate?: string | null;
    }
  ): Promise<PaginatedResponse<Company>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
    });
    if (options?.search?.trim()) {
      params.set("search", options.search.trim());
    }
    if (options?.packageId != null && String(options.packageId).trim() !== "") {
      params.set("packageId", String(options.packageId));
    }
    if (options?.fromDate != null && String(options.fromDate).trim() !== "") {
      params.set("fromDate", String(options.fromDate));
    }
    if (options?.toDate != null && String(options.toDate).trim() !== "") {
      params.set("toDate", String(options.toDate));
    }

    const response = await apiService.get<PaginatedResponse<Company>>(
      `/company/GetPageList?${params.toString()}`
    );

    return normalizePaginatedResponse<Company>(response, {
      page,
      limit,
      fallbackMessage: "Failed to load companies",
    });
  }

  async getById(id: string): Promise<Company> {
    const response = await apiService.get<any>(`/company/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const companyData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;

    const encryptedId =
      (payload as any)?.encryptedId ??
      (response as any)?.encryptedId ??
      (companyData as any)?.encryptedId ??
      null;

    if (companyData) {
      return {
        ...normalizeCompanyPayload(companyData as Record<string, unknown>),
        encryptedId,
      } as Company;
    }

    return response as unknown as Company;
  }

  async getCompanyInfoById(id: string): Promise<Company> {
    const response = await apiService.get<any>(`/company/${id}/GetCompanyWithInfo`);
    const payload: any = (response as any)?.data ?? response;

    const companyData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;
      if (companyData) {
        return normalizeCompanyPayload(companyData as Record<string, unknown>);
      }
    return response as unknown as Company;
  }

  async getByPublicKey(key: string): Promise<Company> {
    const response = await apiService.get<any>(
      `/company/Encrypted/${encodeURIComponent(key)}`
    );
    const payload = (response as any)?.data || response;
    if (payload && typeof payload === "object") {
      if (payload.data) {
        return {
          ...(payload.data as Company),
          encryptedId: payload.encryptedId,
        } as Company;
      }
      return payload as Company;
    }
    return response as unknown as Company;
  }

  async getPackageInfo(id: string): Promise<{ validityMonths?: number; numberOfUsers?: number; packageName?: string }> {
    const response = await apiService.get<any>(`/company/${id}/GetPackageInfo`);
    const payload: any = (response as any)?.data ?? response;
    if (payload && typeof payload === "object") {
      const data = (payload as any).data ?? payload;
      const pkg = data?.package ?? data;
      const numberOfUsers = (data as any)?.numberOfUsers ?? pkg?.numberOfUsers;
      const packageName = (data as any)?.name ?? pkg?.name;
      const details = pkg?.details ?? [];
      const firstDetail = Array.isArray(details) ? details[0] : null;
      const validityDays = firstDetail?.validityDays ?? (pkg as any)?.validityDays;
      const validityMonths =
        validityDays != null ? Math.round(Number(validityDays) / 30) : undefined;
      return { validityMonths, numberOfUsers, packageName };
    }
    return {};
  }

  async create(payload: Partial<Company>): Promise<Company> {
    const response = await apiService.post<Company>(`/company`, payload, undefined, {
      timeout: 90_000,
    });
    return unwrapEntity<Company>(response);
  }

  async uploadImage(companyId: string, imageData: string): Promise<Company> {
    const response = await apiService.post<Company>(
      `/company/${companyId}/image`,
      {
        imageData,
      }
    );
    return unwrapEntity<Company>(response);
  }

  async update(id: string, payload: Partial<Company>): Promise<Company> {
    const response = await apiService.put<Company>(`/company/${id}`, payload, {
      timeout: 90_000,
    });
    return unwrapEntity<Company>(response);
  }

  /** Final registration step — does not use dashboard JWT (works while logged in elsewhere). */
  async completeRegistration(
    id: string,
    payload: Partial<Company>
  ): Promise<Company> {
    const response = await api_no_token_Service.put<Company>(
      `/auth/CompleteCompanyRegistration/${id}`,
      payload
    );
    return unwrapEntity<Company>(response);
  }

  async uploadLicense(id: string, fileName: string, contentBase64: string): Promise<void> {
    const response = await apiService.put(`/company/${id}/license`, {
      fileName,
      content: contentBase64,
    });
    unwrapOrThrow(response, "Subscription upload failed");
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/company/${id}`);
    unwrapOrThrow(response, "Delete failed");
  }

  async setAccountActive(id: string, isActive: boolean): Promise<void> {
    const response = await apiService.put(`/company/${id}/account-active`, {
      isActive,
    });
    unwrapOrThrow(response, "Failed to update account status");
  }

  async triggerPasswordReset(id: string): Promise<void> {
    const response = await apiService.post(`/company/${id}/password-reset`, {});
    unwrapOrThrow(response, "Failed to send password reset");
  }
}

export const companiesService = new CompaniesService();
export default companiesService;
